// Generate mermaid graph text from a workflow's job list. Pure
// (testable) — caller hands the result to mermaid.render() to get SVG.
//
// The job graph is uniform across workflow kinds:
//   - jobs[] sorted by stage_index, then by params (month-ordered)
//   - one subgraph per stage; jobs within a stage are independent
//   - cross-stage transitions imply a barrier (all of stage N must
//     finish before any of stage N+1 starts) — modelled as a single
//     edge from each stage subgraph to the next
//
// Used for batch_export, batch_onboard, batch_rebuild, month_*, year_*,
// scan, etc. — anything the operator persists as a Workflow.

import { WorkflowDetail, WorkflowJob } from "./workflows";

// Map a job status string to a CSS-class name we'll define in the
// diagram itself (mermaid classDef). Anything we don't recognize gets
// the neutral "pending" style.
function styleClassFor(status: string): string {
  switch (status) {
    case "succeeded":
      return "done";
    case "failed":
      return "failed";
    case "cancelled":
      return "cancelled";
    case "running":
      return "running";
    case "starting":
    case "queued":
      return "starting";
    default:
      return "pending";
  }
}

// Pull a short human-readable label from a Job's params: e.g. the
// YYYY-MM the job is processing. Different kinds park that info in
// different fields of params_json:
//   - index/rollup: "input": "s3://bucket/content_type=…/year=YYYY/month=MM/"
//   - export: "start": "YYYY-MM-DD"
//   - scan: nothing month-specific; just say "scan"
//
// Returns the kind itself if we can't derive anything finer-grained.
function jobLabel(job: WorkflowJob): string {
  let params: { input?: string; start?: string };
  try {
    params = JSON.parse(job.params_json) as typeof params;
  } catch {
    return job.kind;
  }
  if (typeof params.input === "string") {
    // …/year=YYYY/month=MM/ → YYYY-MM (Hive-style partition path)
    const m = params.input.match(/year=(\d{4})\/month=(\d{2})/);
    if (m) return `${m[1]}-${m[2]}`;
  }
  if (typeof params.start === "string") {
    // YYYY-MM-DD → YYYY-MM
    return params.start.slice(0, 7);
  }
  return job.kind;
}

// Stable, mermaid-safe node id derived from job id. Mermaid is picky
// about node ids — keep it ASCII-alnum + underscore, no dashes.
function nodeId(job: WorkflowJob): string {
  return `J_${job.id.replace(/[^a-zA-Z0-9]/g, "_")}`;
}

const STAGE_PALETTE = [
  "Stage 1",
  "Stage 2",
  "Stage 3",
  "Stage 4",
];

// Per workflow kind, name each stage with what the jobs in it do.
// Falls back to "Stage N" + the job kind when the workflow's specific
// stage layout isn't known.
function stageHeading(workflowKind: string, stageIdx: number, jobKinds: Set<string>): string {
  const generic = STAGE_PALETTE[stageIdx] ?? `Stage ${stageIdx + 1}`;
  const kind = [...jobKinds].sort().join("/");
  // batch_onboard / batch_rebuild / year_*: stage 0 = index, stage 1 = rollup
  if (
    (workflowKind === "batch_onboard" ||
      workflowKind === "batch_rebuild" ||
      workflowKind === "year_onboard" ||
      workflowKind === "year_rebuild" ||
      workflowKind === "month_onboard" ||
      workflowKind === "month_rebuild") &&
    (kind === "index" || kind === "rollup")
  ) {
    return `${generic} — ${kind === "index" ? "Index" : "Roll up"}`;
  }
  // batch_export / month_export: single stage of export jobs
  if ((workflowKind === "batch_export" || workflowKind === "month_export") && kind === "export") {
    return `${generic} — Export`;
  }
  return `${generic} — ${kind || "jobs"}`;
}

// Quote a label so mermaid handles spaces/punct correctly.
function q(s: string): string {
  return `"${s.replace(/"/g, '\\"')}"`;
}

export function workflowDiagram(wf: WorkflowDetail): string {
  // Group jobs by stage. Within a stage, sort by label for stable rendering.
  const byStage = new Map<number, WorkflowJob[]>();
  for (const j of wf.jobs) {
    const list = byStage.get(j.stage_index) ?? [];
    list.push(j);
    byStage.set(j.stage_index, list);
  }
  const stages = [...byStage.keys()].sort((a, b) => a - b);

  const lines: string[] = [];
  // Outer TD (top-down): stages stack vertically so a year-wide
  // batch_rebuild reads as Index → Roll up flowing down the page.
  // Inner subgraphs use direction LR so 12-13 month nodes fan out
  // horizontally within a stage, using the page's width rather than
  // squashing everything into a single horizontal stripe.
  lines.push("flowchart TD");
  // classDef styling — the colors are tuned to read against both
  // light and dark MUI themes (intentionally muted; mermaid's default
  // hard greens/reds clash with the Insight palette).
  lines.push("  classDef done fill:#2e7d32,stroke:#1b5e20,color:#fff");
  lines.push("  classDef failed fill:#c62828,stroke:#7f1d1d,color:#fff");
  lines.push("  classDef cancelled fill:#ed6c02,stroke:#9a3412,color:#fff");
  lines.push("  classDef running fill:#1565c0,stroke:#0d47a1,color:#fff");
  lines.push("  classDef starting fill:#546e7a,stroke:#37474f,color:#fff");
  lines.push("  classDef pending fill:#9e9e9e,stroke:#616161,color:#fff");

  // Build subgraphs per stage. Mermaid subgraph IDs must also be alnum.
  const stageIdOf = (i: number) => `Stage_${i}`;

  for (const stageIdx of stages) {
    const jobs = (byStage.get(stageIdx) ?? []).sort((a, b) =>
      jobLabel(a).localeCompare(jobLabel(b)),
    );
    const kinds = new Set(jobs.map((j) => j.kind));
    const heading = stageHeading(wf.kind, stageIdx, kinds);
    lines.push(`  subgraph ${stageIdOf(stageIdx)}[${q(heading)}]`);
    lines.push("    direction LR");
    for (const j of jobs) {
      const id = nodeId(j);
      lines.push(`    ${id}[${q(jobLabel(j))}]:::${styleClassFor(j.status)}`);
    }
    // Invisible edges between consecutive siblings force the layout
    // engine (dagre or ELK) to honor `direction LR` even when the
    // jobs are otherwise independent. Without these, both renderers
    // silently fall back to TB inside an edgeless subgraph.
    for (let i = 1; i < jobs.length; i++) {
      lines.push(`    ${nodeId(jobs[i - 1])} ~~~ ${nodeId(jobs[i])}`);
    }
    lines.push("  end");
  }

  // Stage-to-stage edges: a single edge between stages communicates
  // the orchestrator's barrier without N×M fan-in/out lines.
  for (let i = 1; i < stages.length; i++) {
    lines.push(`  ${stageIdOf(stages[i - 1])} --> ${stageIdOf(stages[i])}`);
  }

  return lines.join("\n");
}
